import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { arraysToObjects } from '../array-fix/functions/array-to-object';
import { objectsToArrays } from '../array-fix/functions/object-to-array';
import { ServiceProvider } from '../classes/service-provider';
import { DynamicService } from '../dynamic/services/dynamic.service';
import { easyFormatter } from '../easyFormatter';
import {
  ServiceProvider as ServiceProviderModel,
  ServiceProviderRequest,
  ServiceProviderResponse,
} from '../models/service-provider';
import _ from 'lodash';
import { Service } from '../classes/service';
import { UserService } from './user.service';
import { SiteService } from './site.service';

@Injectable({
  providedIn: 'root',
})
export class ServiceProviderService {
  allServiceProviders$!: Observable<ServiceProvider[]>;
  globalServiceProviders$!: Observable<ServiceProvider[]>;

  private allServiceProvidersSource!: BehaviorSubject<ServiceProvider[]>;
  public globalServiceProvidersSource!: BehaviorSubject<ServiceProvider[]>;

  get allServiceProvidersSourceValue() {
    return this.allServiceProvidersSource.value;
  }

  constructor(
    private siteService: SiteService,
    private http: HttpClient,
    private dynamicService: DynamicService,
    private userService: UserService
  ) {
    this.allServiceProvidersSource = new BehaviorSubject<ServiceProvider[]>([]);
    this.allServiceProviders$ = this.allServiceProvidersSource.asObservable();

    this.globalServiceProvidersSource = new BehaviorSubject<ServiceProvider[]>([]);
    this.globalServiceProviders$ = this.globalServiceProvidersSource.asObservable();
  }

  getAll(): Observable<ServiceProvider[]> {
    const url = environment.api.host + environment.api.paths.api.serviceProvider.get;

    this.siteService.addSubscriptionLog(
      this,
      'service-provider.service.ts->getAll->this.http.get<ServiceProviderResponse>(url)'
    );

    return this.http.get<ServiceProviderResponse>(url).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'service-provider.service.ts->getAll->this.http.get<ServiceProviderResponse>(url)'
        )
      ),
      map((res) => {
        const serviceProviders = (res.data as ServiceProviderModel[]).map((d) => {
          const serviceProvider = new ServiceProvider(
            this.siteService,
            this.http,
            this.dynamicService,
            this.userService,
            objectsToArrays(d)
          );

          serviceProvider.isPartial = true;

          return serviceProvider;
        });

        this.allServiceProvidersSource.next(serviceProviders);

        this.globalServiceProvidersSource.next(
          serviceProviders.filter((service) => service.isGlobal)
        );

        return serviceProviders;
      })
    );
  }

  create(): ServiceProvider {
    let serviceProviders = this.allServiceProvidersSource.value;

    const serviceProvider = new ServiceProvider(
      this.siteService,
      this.http,
      this.dynamicService,
      this.userService
    );

    serviceProviders.push(serviceProvider);

    this.allServiceProvidersSource.next(serviceProviders);

    return serviceProvider;
  }

  /** Providers removed from Conductor (soft-deleted): Content Managers and Operations can restore them. */
  removed(): Observable<Array<{ id: number; removedAt: string | null; data: any }>> {
    const url = environment.api.host + environment.api.paths.api.serviceProvider.get + '/removed';
    return this.http.get<any>(url).pipe(map((r) => r.data));
  }

  /** A removed provider back in Conductor: the same record. Refreshes the provider list. */
  restore(id: number): Observable<{ restored: number }> {
    const url = environment.api.host + environment.api.paths.api.serviceProvider.get + `/${id}/restore`;
    return this.http.post<any>(url, {}).pipe(
      map((r) => r.data),
      tap(() => this.refreshSource())
    );
  }

  refreshSource(): Promise<ServiceProvider[]> {
    return this.getAll().toPromise();
  }

  getServiceById(serviceProviderId: number, serviceId: string): Service | null {
    const serviceProvider = _.find(
      this.allServiceProvidersSourceValue,
      (serviceProvider) => serviceProviderId === serviceProvider.id
    );

    if (serviceProvider) {
      const service: Service | null = serviceProvider.getServiceById(serviceId);

      return service;
    }

    return null;
  }

  get(id: number): Observable<ServiceProviderModel> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.getById,
      { serviceId: id.toString() }
    );

    this.siteService.addSubscriptionLog(
      this,
      'service-provider.service.ts->get->this.http.get<ServiceProviderResponse>(url)'
    );

    return this.http.get<ServiceProviderResponse>(url).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'service-provider.service.ts->get->this.http.get<ServiceProviderResponse>(url)'
        )
      ),
      map((res) => {
        const serviceProvider = res.data as ServiceProviderModel;

        return serviceProvider;
      })
    );
  }

  post(data: any): Observable<ServiceProvider> {
    const url = environment.api.host + environment.api.paths.api.serviceProvider.post;

    const arrayToObject = arraysToObjects(data);

    this.siteService.addSubscriptionLog(
      this,
      'service-provider.service.ts->post->this.http.post<ServiceProviderRequest>(url, arrayToObject)'
    );

    return this.http.post<ServiceProviderRequest>(url, arrayToObject).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'service-provider.service.ts->post->this.http.post<ServiceProviderRequest>(url, arrayToObject)'
        )
      ),
      map((res) => {
        const serviceProvider = res.data as ServiceProvider;

        return serviceProvider;
      })
    );
  }

  patch(id: number, data: any): Observable<ServiceProvider> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.patch,
      { id: id.toString() }
    );

    const arrayToObject = arraysToObjects(data);

    this.siteService.addSubscriptionLog(
      this,
      'service-provider.service.ts->patch->this.http.patch<ServiceProviderRequest>(url, arrayToObject)'
    );

    return this.http.patch<ServiceProviderRequest>(url, arrayToObject).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'service-provider.service.ts->patch->this.http.patch<ServiceProviderRequest>(url, arrayToObject)'
        )
      ),
      map((res) => {
        const serviceProvider = res.data as ServiceProvider;

        return serviceProvider;
      })
    );
  }

  delete(id: number): Observable<boolean> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.delete,
      { id: id.toString() }
    );

    this.siteService.addSubscriptionLog(
      this,
      'service-provider.service.ts->delete->this.http.delete<ServiceProviderRequest>(url)'
    );

    return this.http.delete<ServiceProviderRequest>(url).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'service-provider.service.ts->delete->this.http.delete<ServiceProviderRequest>(url)'
        )
      ),
      map((res) => {
        const bool = res.data as boolean;

        return bool;
      })
    );
  }

  // --- Service provider / item media (documents + images) ---
  // Backed by the generalized documents table. kind: 'document' | 'image'.

  getProviderMedia(serviceId: number, kind?: 'document' | 'image'): Observable<ServiceMedia[]> {
    const base = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.media,
      { id: serviceId.toString() }
    );
    const url = kind ? `${base}?kind=${kind}` : base;
    return this.http
      .get<{ documents: ServiceMedia[] }>(url)
      .pipe(map((res) => res.documents ?? []));
  }

  addProviderMedia(
    serviceId: number,
    fileIds: number[],
    kind: 'document' | 'image'
  ): Observable<ServiceMedia[]> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.media,
      { id: serviceId.toString() }
    );
    return this.http
      .post<{ documents: ServiceMedia[] }>(url, { fileIds, kind })
      .pipe(map((res) => res.documents ?? []));
  }

  // Product media scope: serviceProviderId (numeric) + productSubKey
  // ("<serviceMetaId>:<productMetaId>"). Products have no numeric DB id; their _meta.id is a
  // client string unique only within a service, so the composite is the stable key.
  getItemMedia(
    serviceProviderId: number,
    productSubKey: string,
    kind?: 'document' | 'image'
  ): Observable<ServiceMedia[]> {
    const base = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.product.media,
      { id: serviceProviderId.toString(), productSubKey: encodeURIComponent(productSubKey) }
    );
    const url = kind ? `${base}?kind=${kind}` : base;
    return this.http
      .get<{ documents: ServiceMedia[] }>(url)
      .pipe(map((res) => res.documents ?? []));
  }

  addItemMedia(
    serviceProviderId: number,
    productSubKey: string,
    fileIds: number[],
    kind: 'document' | 'image'
  ): Observable<ServiceMedia[]> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.product.media,
      { id: serviceProviderId.toString(), productSubKey: encodeURIComponent(productSubKey) }
    );
    return this.http
      .post<{ documents: ServiceMedia[] }>(url, { fileIds, kind })
      .pipe(map((res) => res.documents ?? []));
  }

  // Service-type media: scoped to a type (typeKey, e.g. 'venue') within a provider.
  getTypeMedia(
    serviceId: number,
    typeKey: string,
    kind?: 'document' | 'image'
  ): Observable<ServiceMedia[]> {
    const base = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.typeMedia,
      { id: serviceId.toString(), typeKey }
    );
    const url = kind ? `${base}?kind=${kind}` : base;
    return this.http
      .get<{ documents: ServiceMedia[] }>(url)
      .pipe(map((res) => res.documents ?? []));
  }

  addTypeMedia(
    serviceId: number,
    typeKey: string,
    fileIds: number[],
    kind: 'document' | 'image'
  ): Observable<ServiceMedia[]> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.typeMedia,
      { id: serviceId.toString(), typeKey }
    );
    return this.http
      .post<{ documents: ServiceMedia[] }>(url, { fileIds, kind })
      .pipe(map((res) => res.documents ?? []));
  }

  deleteMedia(documentIds: string[]): Observable<boolean> {
    const url = environment.api.host + environment.api.paths.api.serviceProvider.mediaDelete;
    return this.http
      .request<{ success: boolean }>('delete', url, { body: { documentIds } })
      .pipe(map((res) => !!res.success));
  }
}

// A media row from the generalized documents table (service/product scoped).
export interface ServiceMedia {
  id: string;
  entityType: 'service' | 'product' | 'serviceType';
  entityId: number;
  entitySubKey?: string | null;
  kind: 'document' | 'image';
  docType?: string | null;
  sortOrder: number;
  fileId: number;
  _file?: { id: number; name: string; path: string; type: string; size: number };
}
